import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue';
import { MAX_VIEW_NAME_LENGTH, normalizeViewName } from './viewOps';

interface ComponentProps {
  visible: boolean;
  /** "Nový pohled", "Přejmenovat pohled", "Uložit jako nový pohled". */
  heading: string;
  continueLabel: string;
  /** Pre-filled name — the current one when renaming. */
  initialName?: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
}

/** Asks for a tab's name (Figma 21944:65243 / 21944:65327). A blank name is refused, not saved as an empty tab. */
const ViewNameSidebar: React.FC<ComponentProps> = ({ visible, heading, continueLabel, initialName = '', onSubmit, onClose }) => {
  const { tr } = useTranslationLgs();
  const [name, setName] = useState(initialName);
  const [touched, setTouched] = useState(false);

  // Each opening starts from the given name, not from what was typed the last time.
  useEffect(() => {
    if (visible) {
      setName(initialName);
      setTouched(false);
    }
  }, [visible, initialName]);

  const clean = normalizeViewName(name);
  const submit = () => {
    setTouched(true);
    if (!clean) return;
    onSubmit(clean);
    onClose();
  };

  return (
    <RingilSidebar visible={visible} heading={heading} continueLabel={continueLabel} onContinue={submit} onBack={onClose} showSaveButton showBackButton>
      <VerticalFieldAndValue
        label={tr('generic.title', 'Name')}
        labelMode={'normal'}
        required
        // Shown once the user tried to submit, so an empty field doesn't scold them on opening.
        errorMessage={touched && !clean ? tr('validation.required', 'Value is required') : undefined}
      >
        <InputText
          value={name}
          autoFocus
          maxLength={MAX_VIEW_NAME_LENGTH}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          className="w-full"
          data-testid="table-view-name"
        />
      </VerticalFieldAndValue>
    </RingilSidebar>
  );
};

export default ViewNameSidebar;
